import { all, call, put, takeLatest } from 'redux-saga/effects';

import { hideLoader, showLoader } from '@topconagriculture/loader';
import { Action, Resource } from '@topconagriculture/permissions';
import { checkPermissions } from '@topconagriculture/casbin-react';

import { LoadersNames } from '../../ui/ui.constants';
import api from '../../../core/api';
import { composeErrorMessage } from '../../../core/helpers/utils/utils';
import { notificationError, notificationSuccess } from '../../../core/helpers/utils/notificationUtils';
import { getFarmsListRequest } from '../farms/farms.actions';
import {
  createLotMarketType,
  deleteLotMarketType,
  getLotMarketTypes,
  updateLotMarketType,
} from './lotMarketTypes.actions';
import { compose403ErrorMessage, successHandler } from '../../../core/helpers/utils/sagaUtils';

export function* getLotMarketTypesSaga(action: ReturnType<typeof getLotMarketTypes.started>) {
  try {
    yield put(showLoader(LoadersNames.getLotMarketTypes));
    const canReadLotMarketTypes = yield call(checkPermissions, {
      resource: Resource.FEED_SETTINGS_LOTMARKETTYPES,
      action: Action.READ,
    });
    if (canReadLotMarketTypes) {
      const result = yield call(api.lotMarketTypes.getLotMarketTypes);
      yield put(
        getLotMarketTypes.done({
          params: action.payload,
          result,
        }),
      );
    }
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.getLotMarketTypes));
}

export function* createLotMarketTypeSaga(action: ReturnType<typeof createLotMarketType.started>) {
  yield put(showLoader(LoadersNames.createLotMarketType));
  try {
    const { payload, meta } = action;
    const { data, formName } = payload;
    const result = yield call(api.lotMarketTypes.createLotMarketType, data, formName);
    yield put(
      createLotMarketType.done({
        params: payload,
        result,
      }),
    );
    yield put(notificationSuccess('lots:lot_market_type_create_success'));
    if (meta) {
      if (meta.onSuccess && typeof meta.onSuccess === 'function') {
        meta.onSuccess(result);
      }
    }
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.createLotMarketType));
}

export function* updateLotMarketTypeSaga(action: ReturnType<typeof updateLotMarketType.started>) {
  try {
    const { payload, meta } = action;
    const { data, formName } = payload;
    const result = yield call(api.lotMarketTypes.updateLotMarketType, data.id!, data, formName);
    yield put(
      updateLotMarketType.done({
        params: payload,
        result,
      }),
    );
    yield put(notificationSuccess('lots:lot_market_type_edit_success'));
    if (meta && meta.onSuccess && typeof meta.onSuccess === 'function') {
      meta.onSuccess(result);
    }
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
}

export function* deleteLotMarketTypeSaga(action: ReturnType<typeof deleteLotMarketType.started>) {
  try {
    const { id, formName } = action.payload;
    yield call(api.lotMarketTypes.deleteLotMarketType, id, formName);
    yield put(
      deleteLotMarketType.done({
        params: action.payload,
        result: id,
      }),
    );
    successHandler(action.meta);
    yield put(notificationSuccess('lots:lot_market_type_delete_success'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
}

export function* watchLotMarketTypesSaga() {
  yield all([
    yield takeLatest(getFarmsListRequest.done, getLotMarketTypesSaga),
    yield takeLatest(getLotMarketTypes.started, getLotMarketTypesSaga),
    yield takeLatest(createLotMarketType.started, createLotMarketTypeSaga),
    yield takeLatest(updateLotMarketType.started, updateLotMarketTypeSaga),
    yield takeLatest(deleteLotMarketType.started, deleteLotMarketTypeSaga),
  ]);
}
